import { captureException } from "src/utils/error";
import { loadBing } from "./bing";
import { loadFacebookPixel } from "./facebook";
import { loadFullStory } from "./fullstory";
import { loadGA } from "./ga";
import { loadImpact } from "./impact";
import { loadLinkedIn } from "./linkedin";
import { loadLiveIntent } from "./liveintent";
import { loadPinterest } from "./pinterest";
import { loadPodSights } from "./podsights";
import { loadSnapchat } from "./snapchat";
import { loadTag4Arm } from "./tag4arm";
import { loadTikTok } from "./tiktok";
import { loadReddit } from "./reddit";
import { loadRoktPixel } from "src/vendor/rokt";
import { perfTrack } from "src/utils/timing";
import { loadSafeOptPixel } from "src/vendor/safeopt";
import { loadOptimizely } from "./optimizely";
import { appConfig } from "src/config";
import { loadHorizon } from "./horizon";

/**
 * - bootstrap: Occurs as early as possible, immediately after our application code has parsed and loaded.
 * - after-context-init: Occurs after we have performed general data setup (server context, user data, etc)
 * - rendered: Occurs after the user sees visible content on the page
 * - idle: Occurs with slight delay after rendered. On supporting browsers this is tied to actual idle, for others a timeout.
 */
type Phase = "bootstrap" | "after-context-init" | "rendered" | "idle";

const phaseMap: Record<Phase, (() => Promise<any>)[]> = {
  bootstrap: [loadFullStory],
  "after-context-init": [loadFacebookPixel],
  rendered: [loadBing, loadGA, queueIdlePhase],
  idle: [
    loadImpact,
    loadLinkedIn,
    loadLiveIntent,
    loadPinterest,
    loadPodSights,
    loadSnapchat,
    loadTag4Arm,
    loadTikTok,
    loadReddit,
    loadRoktPixel,
    loadSafeOptPixel,
    loadHorizon,
    perfTrack,
  ],
};

const { useOptimizely } = appConfig.meristemConfig;

if (useOptimizely) {
  phaseMap.bootstrap.push(loadOptimizely);
}

async function queueIdlePhase() {
  // Defer idle libs until common FCP timing
  setTimeout(() => {
    // And then wait for actual idle
    if (window.requestIdleCallback) {
      requestIdleCallback(
        () => {
          triggerLoadPhase("idle");
        },
        { timeout: 10_000 }
      );
    } else {
      // Unless we can't
      triggerLoadPhase("idle");
    }
  }, 3000);
}

/**
 * Used to denote key events in the application init lifecycle and to kick off appropriate
 * library loading after that point.
 *
 * Generally this should not be called directly outside of init code.
 */
export function triggerLoadPhase(phase: Phase) {
  const ret = Promise.all(
    phaseMap[phase].map((fn) =>
      fn().catch((err) => {
        // These should not be fatal anywhere.
        captureException(err);
      })
    )
  );

  phaseMap[phase] = [];
  return ret;
}
